import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, Trash2, Sparkles, Music, Loader2 } from 'lucide-react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  addDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useToast } from '@/components/ui/use-toast';

/**
 * Admin in-person walkthrough: writes real `requests` docs for the artist's current live session
 * so they appear on the artist's phone. Flagged `is_admin_demo` — no payment, no stats side-effects
 * (CF skips popularity; earnings logic ignores no payment_intent_id). Push notifications are sent like real requests.
 */
const DEMO_PRESETS = [
  { presetId: 'wonderwall', title: 'Wonderwall', originalArtist: 'Oasis', songAmount: 4, tipAmount: 0 },
  { presetId: 'sweet-caroline', title: 'Sweet Caroline', originalArtist: 'Neil Diamond', songAmount: 3, tipAmount: 0 },
  { presetId: 'sex-on-fire', title: 'Sex on Fire', originalArtist: 'Kings of Leon', songAmount: 3, tipAmount: 2 },
];

export default function AdminArtistDemoPanel({ artistId, artist, songs, artistCcy, fmt }) {
  const { toast } = useToast();
  const [liveSessionId, setLiveSessionId] = useState(null);
  const [isArtistLive, setIsArtistLive] = useState(false);
  const [queueRows, setQueueRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!artistId) return;
    const ref = doc(db, 'live_status', artistId);
    return onSnapshot(ref, (snap) => {
      const d = snap.exists() ? snap.data() : {};
      const live = d.is_live === true;
      const sid = d.session_id || null;
      setIsArtistLive(live);
      setLiveSessionId(sid);
    });
  }, [artistId]);

  const activeCatalogue = useMemo(() => (songs || []).filter((s) => !s.archived), [songs]);

  const pushRequest = useCallback(
    async ({ title, originalArtist, songAmount, tipAmount, songId, presetId }) => {
      if (!artistId || !liveSessionId) {
        toast({
          title: 'Artist must be live',
          description: 'Ask them to tap Go Live on their phone first. Demo lines use their current session.',
          variant: 'destructive',
        });
        return;
      }
      const key = `${presetId || songId || title}-${Date.now()}`;
      setIsSubmitting(true);
      try {
        const tip = Number(tipAmount) || 0;
        const song = Number(songAmount) || 0;
        const docRef = await addDoc(collection(db, 'requests'), {
          artist_id: artistId,
          session_id: liveSessionId,
          song_id: songId || null,
          song_title: title,
          original_artist: originalArtist || 'Unknown',
          amount_paid: song + tip,
          tip,
          requester_name: 'YSR demo',
          note: '',
          status: 'pending',
          is_admin_demo: true,
          is_tip_only: false,
          created_at: serverTimestamp(),
        });
        setQueueRows((prev) => [
          ...prev,
          {
            key,
            firestoreId: docRef.id,
            title,
            originalArtist,
            songAmount: song,
            tipAmount: tip,
            kind: presetId ? 'preset' : 'catalogue',
          },
        ]);
      } catch (e) {
        console.error(e);
        toast({
          title: 'Could not add demo line',
          description: e?.message || 'Check Firestore rules and try again.',
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [artistId, liveSessionId, toast]
  );

  const addPreset = (p) =>
    pushRequest({
      title: p.title,
      originalArtist: p.originalArtist,
      songAmount: p.songAmount,
      tipAmount: p.tipAmount,
      songId: null,
      presetId: p.presetId,
    });

  const addCatalogueSong = (song) =>
    pushRequest({
      title: song.title,
      originalArtist: song.original_artist || song.artist || 'Unknown',
      songAmount: Number(song.price) || 0,
      tipAmount: 0,
      songId: song.id,
      presetId: null,
    });

  const removeRow = async (row) => {
    if (!row.firestoreId) return;
    try {
      await deleteDoc(doc(db, 'requests', row.firestoreId));
      setQueueRows((prev) => prev.filter((r) => r.key !== row.key));
    } catch (e) {
      console.error(e);
      toast({ title: 'Remove failed', description: 'Try again.', variant: 'destructive' });
    }
  };

  const clearTracked = async () => {
    setClearing(true);
    try {
      await Promise.all(queueRows.map((r) => (r.firestoreId ? deleteDoc(doc(db, 'requests', r.firestoreId)) : null)));
      setQueueRows([]);
    } catch (e) {
      console.error(e);
      toast({ title: 'Clear failed', description: e?.message || 'Try again.', variant: 'destructive' });
    } finally {
      setClearing(false);
    }
  };

  const clearAllDemosForArtist = async () => {
    if (!artistId) return;
    setClearing(true);
    try {
      const q = query(collection(db, 'requests'), where('artist_id', '==', artistId));
      const snap = await getDocs(q);
      const toDelete = snap.docs.filter((d) => d.data()?.is_admin_demo === true);
      await Promise.all(toDelete.map((d) => deleteDoc(d.ref)));
      setQueueRows([]);
      toast({ title: 'Cleared', description: `Removed ${toDelete.length} demo line(s) for this artist.` });
    } catch (e) {
      console.error(e);
      toast({ title: 'Clear failed', description: e?.message || 'Try again.', variant: 'destructive' });
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-amber-950/40 border-amber-600/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-amber-100 flex items-center gap-2 text-base">
            <Sparkles className="w-5 h-5 shrink-0" />
            Live queue demo (artist&apos;s phone)
          </CardTitle>
          <CardDescription className="text-amber-200/90 text-sm leading-relaxed">
            When the artist is <strong>Go Live</strong> with an active session, taps here create real-looking lines in
            their <strong>Incoming requests</strong> on this same session — so they see it on their device. Lines are
            tagged internally as admin demos: no payment, no reporting, and popularity counts are not updated.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="outline" className={isArtistLive ? 'border-green-500 text-green-300' : 'border-orange-500 text-orange-200'}>
              {isArtistLive && liveSessionId ? 'Live · session active' : 'Not live — wait for Go Live'}
            </Badge>
            {artistCcy && (
              <Badge variant="outline" className="border-gray-500 text-gray-300">
                {artistCcy}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Tap to send to their queue</h3>

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Example pricing (always here)</p>
            <div className="space-y-2">
              {DEMO_PRESETS.map((p) => (
                <button
                  key={p.presetId}
                  type="button"
                  disabled={!isArtistLive || !liveSessionId || isSubmitting}
                  onClick={() => addPreset(p)}
                  className="w-full text-left rounded-xl border border-purple-600/40 bg-purple-900/25 hover:bg-purple-900/45 disabled:opacity-50 px-4 py-3 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-white font-medium">{p.title}</p>
                      <p className="text-purple-200 text-sm">by {p.originalArtist}</p>
                    </div>
                    <Badge variant="outline" className="border-amber-400/60 text-amber-200 shrink-0">
                      Sample
                    </Badge>
                  </div>
                  <p className="text-purple-100 text-sm mt-2">
                    {fmt(p.songAmount)}
                    {p.tipAmount > 0 ? (
                      <>
                        {' '}
                        + {fmt(p.tipAmount)} tip
                      </>
                    ) : null}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              {artist?.artist_name ? `${artist.artist_name}’s catalogue` : 'Artist catalogue'}
            </p>
            {activeCatalogue.length === 0 ? (
              <p className="text-gray-400 text-sm py-4 border border-dashed border-gray-600 rounded-lg px-4">
                No songs in this catalogue yet — use the three samples above.
              </p>
            ) : (
              <div className="max-h-[320px] overflow-y-auto space-y-2 pr-1">
                {activeCatalogue.map((song) => (
                  <button
                    key={song.id}
                    type="button"
                    disabled={!isArtistLive || !liveSessionId || isSubmitting}
                    onClick={() => addCatalogueSong(song)}
                    className="w-full text-left rounded-lg border border-gray-600 bg-gray-800/80 hover:bg-gray-700/90 disabled:opacity-50 px-3 py-2 transition-colors"
                  >
                    <p className="text-white text-sm font-medium">{song.title}</p>
                    <p className="text-gray-400 text-xs">{song.original_artist || song.artist}</p>
                    <p className="text-gray-300 text-xs mt-1">{fmt(Number(song.price) || 0)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-white">Sent this session (admin)</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-gray-500 text-gray-300"
                disabled={clearing || queueRows.length === 0}
                onClick={clearTracked}
              >
                {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Clear list'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-amber-700/60 text-amber-200"
                disabled={clearing}
                onClick={clearAllDemosForArtist}
              >
                Clear all demos (artist)
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-purple-600/50 bg-purple-900/30 p-4 min-h-[160px]">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-purple-600 rounded-lg flex items-center justify-center">
                <Music className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Mirror of their incoming feed</p>
                <p className="text-purple-200 text-xs">Same documents their dashboard reads</p>
              </div>
            </div>

            {queueRows.length === 0 ? (
              <p className="text-purple-200/80 text-sm text-center py-8">
                Nothing sent from this panel yet in this browser session.
              </p>
            ) : (
              <ul className="space-y-2">
                {queueRows.map((row) => (
                  <li
                    key={row.key}
                    className="flex items-start justify-between gap-2 rounded-xl bg-purple-800/35 border border-purple-700/40 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-white font-medium text-sm truncate">{row.title}</p>
                      <p className="text-purple-200 text-xs">by {row.originalArtist}</p>
                      <p className="text-purple-100 text-xs mt-1">
                        {fmt(row.songAmount)}
                        {row.tipAmount > 0 ? <> + {fmt(row.tipAmount)} tip</> : null}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-200">
                        {row.kind === 'preset' ? 'Sample' : 'Catalogue'}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => removeRow(row)}
                        className="p-1 rounded text-purple-300 hover:text-white hover:bg-purple-700/50"
                        aria-label="Remove demo request"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-xs text-gray-500 flex items-start gap-2">
            <User className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            If they paused new requests, demo lines still appear so you can talk through the UI. They can mark these as
            played like any other line — no earnings or referral impact.
          </p>
        </div>
      </div>
    </div>
  );
}
